import {
  prisma,
  type Prisma,
  ResourceType,
  ActionType,
  IntegrationType,
  GatewayType,
} from "client";
import { setNewWhiteLabelWithDefaultTemplates } from "../defaultTemplate";
import { clonePlatformTemplateV2ToWhiteLabel } from "../templateV2";
import {
  assertSingleOrganizationOwner,
  ORGANIZATION_OWNER_ROLE_NAME,
} from "../user/organization-owner-helpers";
import createDefaultCommunicationChannel from "./defaultCommunicationChannel";

async function connectCashAndFreePaymentGatewaysToWhiteLabel(
  whiteLabelId: string
) {
  const gateways = await prisma.paymentGateway.findMany({
    where: {
      type: { in: [GatewayType.CASH, GatewayType.FREE] },
      disabled: false,
    },
    select: { id: true },
  });

  if (gateways.length === 0) return;

  await prisma.whiteLabel.update({
    where: { id: whiteLabelId },
    data: {
      paymentGateways: {
        connect: gateways.map((g) => ({ id: g.id })),
      },
    },
  });
}

// READ
export const getAllWhiteLabels = async (
  userId: string,
  payload?: Prisma.WhiteLabelFindManyArgs
) => {
  try {
    const whiteLabels = await prisma.whiteLabel.findMany({
      ...payload,
    });
    return whiteLabels;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getAllWhiteLabelsCount = async (
  userId: string,
  payload?: Prisma.WhiteLabelCountArgs
) => {
  try {
    const count = await prisma.whiteLabel.count({
      ...payload,
    });

    return count;
  } catch (error: any) {
    return error;
  }
};

export const getWhiteLabelsWithQueueConfiguration = async () => {
  const whiteLabels = await prisma.whiteLabel.findMany({
    where: { queueConfigurations: { some: { isActive: true } } },
    select: { id: true },
  });
  return whiteLabels;
};

export const getWhiteLabelById = async (
  userId: string,
  id: string,
  payload?: Prisma.WhiteLabelFindUniqueArgs,
  include?: Prisma.WhiteLabelInclude
) => {
  const whiteLabel = (await prisma.whiteLabel.findUnique({
    where: { id },
    ...payload,
    include: {
      branding: true,
      ...include,
    },
  })) as Prisma.WhiteLabelGetPayload<{ include: { branding: true } }>;

  return {
    ...whiteLabel,
    ...whiteLabel?.branding,
    id: whiteLabel?.id,
  };
};

export const getWhiteLabelByIdPublic = async (
  id: string,
  payload?: Prisma.WhiteLabelFindUniqueArgs
) => {
  const whiteLabel = (await prisma.whiteLabel.findUnique({
    where: { id },
    ...payload,
    include: {
      branding: {
        select: {
          logo: true,
        },
      },
    },
  })) as Prisma.WhiteLabelGetPayload<{ include: { branding: true } }>;

  return {
    ...whiteLabel,
    id: whiteLabel?.id,
    logo: whiteLabel?.branding?.logo,
  };
};

export const findUniqueWhiteLabelBySubdomain = async (subdomain: string) => {
  try {
    const whiteLabel = await prisma.whiteLabel.findUnique({
      where: { subdomain },
      include: {
        branding: true,
      },
    });

    return {
      ...whiteLabel,
      ...whiteLabel?.branding,
      id: whiteLabel?.id,
    };
  } catch (error) {
    console.error(error);
  }
};

export const getWhiteLabelIdBySubdomain = async (subdomain: string) => {
  const row = await prisma.whiteLabel.findUnique({
    where: { subdomain },
    select: { id: true },
  });
  return row?.id ?? null;
};

export const getWhiteLabelIdByCustomDomain = async (domain: string) => {
  const row = await prisma.whiteLabel.findFirst({
    where: { customDomain: domain },
    select: { id: true },
  });
  return row?.id ?? null;
};

export const findUniqueWhiteLabelByCustomDomain = async (domain: string) => {
  const whiteLabel = await prisma.whiteLabel.findFirst({
    where: {
      customDomain: domain,
    },
    include: {
      branding: true,
    },
  });
  return {
    ...whiteLabel,
    ...whiteLabel?.branding,
    id: whiteLabel?.id,
  };
};

export const createWhiteLabel = async (
  payload: Prisma.WhiteLabelCreateInput & {
    organization?: {
      organizationPayload: Prisma.OrganizationCreateInput;
      translations: { name: string; languageId: string }[];
      userPayload: {
        name: string;
        email: string;
        invitationToken?: string;
        invitationCreatedAt?: Date;
        invitationSentAt?: Date;
      };
      token?: string;
    };
  },
  createdById: string
) => {
  try {
    if (payload.customDomain) {
      const existingCustomDomain = await prisma.whiteLabel.findFirst({
        where: { customDomain: payload.customDomain },
      });
      if (existingCustomDomain)
        throw new Error(`This custom domain is already taken`);
    }
    if (payload.subdomain) {
      const existingSubdomain = await prisma.whiteLabel.findFirst({
        where: { subdomain: payload.subdomain },
      });
      if (existingSubdomain) throw new Error(`This Subdomain is already taken`);
    }

    console.log("Creating WhiteLabel with payload:", payload);
    console.log("Created by user ID:", createdById);

    const { organization, ...whiteLabelPayload } = payload;

    const { response } = await prisma.$transaction(async (tx) => {
      const response = await tx.whiteLabel.create({
        data: {
          ...whiteLabelPayload,
          createdBy: {
            connect: {
              id: createdById,
            },
          },
        },
      });

      if (organization) {
        const orgSlugRaw = organization.organizationPayload.slug;
        const orgSlug = orgSlugRaw
          ? orgSlugRaw.trim().toLowerCase()
          : undefined;
        if (orgSlug) {
          const existingOrg = await tx.organization.findUnique({
            where: {
              slug_whiteLabelId: {
                slug: orgSlug,
                whiteLabelId: response.id,
              },
            },
            select: { id: true },
          });
          if (existingOrg) {
            throw new Error(
              "An organization with this slug already exists in this white-label. Please choose a different slug."
            );
          }
        }

        const createdOrganization = await tx.organization.create({
          data: {
            ...organization.organizationPayload,
            ...(orgSlug ? { slug: orgSlug } : {}),
            defaultWhiteLabelOrganization: true,
            createdBy: {
              connect: {
                id: createdById,
              },
            },
            whiteLabel: {
              connect: {
                id: response.id,
              },
            },
            translations: {
              createMany: {
                data: organization.translations,
              },
            },
          },
        });

        const ownerRole = await tx.roleV2.findUnique({
          where: { name: ORGANIZATION_OWNER_ROLE_NAME },
        });

        if (!ownerRole) {
          throw new Error(`${ORGANIZATION_OWNER_ROLE_NAME} role not found`);
        }

        const existingUser = await tx.user.findUnique({
          where: {
            email_accessibleWhiteLabelId: {
              email: organization.userPayload.email.toLowerCase(),
              accessibleWhiteLabelId: response.id,
            },
          },
        });

        await assertSingleOrganizationOwner(
          ownerRole.id,
          [createdOrganization.id],
          existingUser?.id,
          tx
        );

        if (existingUser) {
          await tx.user.update({
            where: { id: existingUser.id },
            data: {
              roleV2: {
                connect: { id: ownerRole.id },
              },
              organizations: {
                connect: [{ id: createdOrganization.id }],
              },
            },
          });
        } else {
          await tx.user.create({
            data: {
              name: organization.userPayload.name,
              email: organization.userPayload.email.toLowerCase(),
              invitationToken: organization.userPayload.invitationToken,
              invitationCreatedAt: organization.userPayload.invitationCreatedAt,
              invitationSentAt: organization.userPayload.invitationSentAt,
              accessibleWhiteLabel: {
                connect: { id: response.id },
              },
              roleV2: {
                connect: { id: ownerRole.id },
              },
              organizations: {
                connect: [{ id: createdOrganization.id }],
              },
            },
          });
        }
      }

      return { response };
    });

    await connectCashAndFreePaymentGatewaysToWhiteLabel(response.id);

    await prisma.integration.create({
      data: {
        whiteLabel: {
          connect: {
            id: response.id,
          },
        },
        type: IntegrationType.CAPTCHA,
        isActive: false,
        settings: {
          siteKey: process.env.NEXT_PUBLIC_CAPTCHA_PUBLIC_KEY,
          secretKey: process.env.CAPTCHA_SECRET_KEY,
        },
      },
    });
    const whiteLabelWithTemplates = await setNewWhiteLabelWithDefaultTemplates(
      createdById,
      response.id
    );
    await clonePlatformTemplateV2ToWhiteLabel(response.id, createdById);
    await createDefaultCommunicationChannel(createdById, response.id);
    return response;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

export const updateWhiteLabel = async (
  userId: string,
  id: string,

  payload: Prisma.WhiteLabelUpdateInput
) => {
  try {
    const response = await prisma.whiteLabel.update({
      where: {
        id,
      },
      data: {
        ...payload,
      },
    });

    return response;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};
export const deleteWhiteLabel = async (userId: string, id: string) => {
  try {
    const events = await prisma.event.findMany({
      where: {
        whiteLabelId: id,
      },
      select: {
        id: true,
      },
    });

    const eventIds = events.map((event) => event.id);

    await prisma.eventTranslations.deleteMany({
      where: {
        eventId: {
          in: eventIds,
        },
      },
    });

    await prisma.event.deleteMany({
      where: {
        whiteLabelId: id,
      },
    });
    const response = await prisma.whiteLabel.update({
      where: {
        id: id,
      },
      data: {
        deletedAt: new Date(),
      },
    });
    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getWhiteLabelByCountry = async (
  country: "UAE" | "KSA" | "JO" | "International"
) => {
  console.log("country", country);
  const whiteLabel = await prisma.whiteLabel.findFirst({
    where: {
      name: {
        contains: country,
        mode: "insensitive",
      },
    },
  });

  return whiteLabel;
};

export const getWhiteLabelDTCMIntegrationKeys = async (id: string) => {
  try {
    const whiteLabel = await prisma.whiteLabel.findUnique({
      where: {
        id,
      },
      include: {
        integrations: {
          where: {
            type: IntegrationType.DTCM,
          },
          orderBy: {
            createdAt: "desc",
          },
        },
      },
    });
    return whiteLabel?.integrations?.[0].settings || {};
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

export const hasDTCMIntegration = async (id: string): Promise<boolean> => {
  try {
    const whiteLabel = await prisma.whiteLabel.findUnique({
      where: { id },
      select: {
        integrations: {
          where: {
            type: IntegrationType.DTCM,
          },
          select: { id: true },
          take: 1,
        },
      },
    });

    return !!whiteLabel?.integrations.length;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

export const getWhiteLabelByEventId = async (eventId: string) => {
  try {
    const event = await prisma.event.findUnique({
      where: { id: eventId },
      include: { whiteLabel: true },
    });
    if (!event) throw new Error("Event not found");
    return event?.whiteLabel;
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

/** Event PK only — WhiteLabel has no eventId; callers that need the id should use this. */
export const getWhiteLabelIdByEventId = async (eventId: string) => {
  const event = await prisma.event.findUnique({
    where: { id: eventId },
    select: { whiteLabelId: true },
  });
  return event?.whiteLabelId ?? null;
};
